import { useCallback, useEffect, useState } from "react";

import { Button, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import { OutletPaymentMethodForm } from "@/features/outlet-payment-method";
import { OutletRoleEnum } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, PageContainer, useSnackbar } from "@/features/ui";

export const OutletPaymentMethod = () => {
  const { outletId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [outlet, setOutlet] = useState(null);
  const [defaultValues, setDefaultValues] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isRetail = outlet?.role === OutletRoleEnum.RETAIL;
  const canEdit = hasPermission(PermissionEnum.UPDATE_OUTLETS) && isRetail;

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        {
          data: { data: outletData },
        },
        {
          data: { data: associations },
        },
      ] = await Promise.all([
        apiClient.get(`outlets/${outletId}`),
        apiOutletClient.get("payment-methods", {
          headers: {
            "Outlet-Id": outletId,
          },
        }),
      ]);

      setOutlet(outletData);
      setDefaultValues({
        paymentMethods: associations.map((assoc) => ({
          paymentMethod: { id: assoc.id, name: assoc.name },
          outletChartOfAccount: assoc.outletChartOfAccount,
        })),
      });
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat data metode pembayaran"));
    } finally {
      setIsLoading(false);
    }
  }, [outletId, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (data) => {
    try {
      await apiOutletClient.post(
        "payment-methods",
        {
          paymentMethods: data.paymentMethods.map((row) => ({
            paymentMethodId: row.paymentMethod.id,
            cashBankOutletChartOfAccountId: row.outletChartOfAccount.id,
          })),
        },
        {
          headers: {
            "Outlet-Id": outletId,
          },
        },
      );
      navigate(-1);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memuat metode pembayaran outlet"),
      );
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  const pageTitle = outlet
    ? `Metode Pembayaran — ${outlet.name}`
    : "Metode Pembayaran";

  const breadcrumbs = outlet
    ? [
        { title: "Cabang Toko", path: "/setting/outlets" },
        { title: outlet.name },
        { title: "Metode Pembayaran" },
      ]
    : [
        { title: "Cabang Toko", path: "/setting/outlets" },
        { title: "Metode Pembayaran" },
      ];

  if (outlet && !isRetail) {
    return (
      <PageContainer maxWidth="xl" title={pageTitle} breadcrumbs={breadcrumbs}>
        <Typography variant="body1" color="text.secondary">
          Pengaturan metode pembayaran hanya tersedia untuk cabang toko Retail.
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl" title={pageTitle} breadcrumbs={breadcrumbs}>
      <OutletPaymentMethodForm
        outletId={outletId}
        defaultValues={defaultValues}
        isLoading={isLoading}
        disabled={!canEdit}
        onSubmit={handleSubmit}
        renderActionButton={
          hasPermission(PermissionEnum.UPSERT_USER_OUTLET) && canEdit
            ? ({ isSubmitting }) => (
                <Button
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  sx={{ alignSelf: "flex-end" }}
                >
                  Simpan
                </Button>
              )
            : undefined
        }
      />
    </PageContainer>
  );
};
